/* eslint-disable no-console */
import { OTP_OPTIONS } from './consts';

/**
 * OTP utility functions for validation, prioritization, and error handling
 */

export interface OTPValidationResult {
  isValid: boolean;
  error?: string;
}

export interface OTPVerificationResult {
  success?: boolean;
  verified: boolean;
  remainingCount?: string;
  errorCode?: string;
  errorMessage?: string;
  error?: string;
}

/**
 * Validates if the verification code is in the correct format
 */
export const validateVerificationCode = (code: string): OTPValidationResult => {
  if (!code) {
    return { isValid: false, error: 'Verification code is required' };
  }

  if (code.length !== 6) {
    return { isValid: false, error: 'Verification code must be 6 digits' };
  }

  if (!/^\d{6}$/.test(code)) {
    return { isValid: false, error: 'Verification code must contain only digits' };
  }

  return { isValid: true };
};

/**
 * Validates required OTP parameters
 * - userEmail and jwt are always required
 * - userId and authID are only validated if explicitly provided (not undefined)
 */
export const validateOTPRequiredParams = (params: {
  userEmail?: string | null;
  userId?: string | null;
  jwt?: string | null;
  authID?: string | null;
}): OTPValidationResult => {
  const { userEmail, userId, jwt, authID } = params;

  // Always required parameters
  if (!userEmail || userEmail.trim() === '') {
    return { isValid: false, error: 'User email is missing or null' };
  }

  if (!jwt || jwt.trim() === '') {
    return { isValid: false, error: 'JWT token is missing or null' };
  }

  // Conditionally required parameters (only validate if explicitly provided)
  if (userId !== undefined && (!userId || userId.trim() === '')) {
    return { isValid: false, error: 'User ID is missing or null' };
  }

  if (authID !== undefined && (!authID || authID.trim() === '')) {
    return { isValid: false, error: 'Auth ID is missing' };
  }

  return { isValid: true };
};

/**
 * Formats and validates numeric input for verification code
 */
export const formatVerificationCodeInput = (value: string, maxLength: number = 6): string => {
  const numericValue = value.replace(/\D/g, ''); // Remove non-digits
  return numericValue.slice(0, maxLength); // Limit to max length
};

/**
 * Gets the highest priority OTP option from available options
 */
export const getPriorityOTPOption = (options: string[]): string | null => {
  // Check if options is an array
  if (!Array.isArray(options)) {
    console.error('getPriorityOTPOption: options is not an array:', typeof options, options);
    return null;
  }

  const priorityOrder = [OTP_OPTIONS.SMS, OTP_OPTIONS.PHONE, OTP_OPTIONS.EMAIL];
  return priorityOrder.find((priority) => options.includes(priority)) || null;
};

/**
 * Logs OTP-related errors with consistent formatting
 */
export const logOTPError = (operation: string, error: string, context?: Record<string, unknown>): void => {
  const message = `${operation}: ${error}`;
  if (context) {
    console.error(message, context);
  } else {
    console.error(message);
  }
};

/**
 * Formats phone number for display (masks middle digits)
 */
export const formatDisplayPhoneNumber = (phone: string | null | undefined): string | null => {
  if (!phone) return null;
  return `(***)-***-${phone}`;
};
